










import {
  defineComponent, ref,
} from '@vue/composition-api';

import { getAdvData } from '@/components/adv-components/adv-common-api';
import SwipeBanner from '@/components/swiper-banner/index.vue';
import { openThirdPage } from '@/api/native/utils';
import { ubtSendError } from '@/shared/utils/ubt';
import { listenAppBackToApp } from '@/shared/utils/backListener';

export default defineComponent({
  props: {
    advId: String,
  },
  components: {
    SwipeBanner,
  },
  emits: [
    'init',
    'indicator-change',
    'click-item',
  ],
  setup(props, { emit }) {
    /**
     * 定义用于存储轮播图列表的响应式引用。
     * 该引用将用于管理和更新轮播图的数据。
     */
    const swiperBannerList = ref<{
      imageUrl: string;
      url: string;
      id: string;
      name: string;
    }[]>();
    /**
     * 定义用于存储广告数据的响应式引用。
     * 该引用将用于管理和更新广告相关的数据。
     */
    const advData = ref();
    /**
     * 定义用于存储当前轮播图索引的响应式引用。
     * 该引用将用于跟踪和更新当前显示的轮播图项。
     */
    const currentIndex = ref(0);
    const loopTime = 3000;

    /** 是否自动轮播，这里是为了处理，在点击banner链接跳出后，停止轮播，防止不必要的曝光数据 */
    const autoplay = ref<number | undefined>(loopTime);

    const getTrackingParams = () => {
      const advItem = advData.value[currentIndex.value]?.tracker as Dictionary;
      if (!advItem) {
        return {};
      }

      const {
        task_id: taskId,
        task_name: taskName,
        child_task_id: childrenTaskId,
        child_task_name: childrenTaskName,
      } = advItem;
      return {
        /** 当前创建的任务ID，对应广告平台中的任务 */
        taskId,
        /** 当前创建的任务名称，对应广告平台中的任务名称 */
        taskName,
        /** 当前创建的子任务ID，对应广告平台中的任务中创建的一条展示子任务，如轮播的图片组件，则是其中一条轮播任务id */
        childrenTaskId,
        /** 当前创建的子任务名称，对应广告平台中的任务中创建的一条展示子任务，如轮播的图片组件，则是其中一条轮播任务名称 */
        childrenTaskName,
        /** 轮播位置 */
        rollNum: currentIndex.value,
      };
    };

    const initData = () => {
      const {
        advId,
      } = props;

      if (!advId || advId.trim() === '') {
        return;
      }

      getAdvData(advId)
        .then((data: { creative: Dictionary[] }) => {
          // 如果没有数据，则什么都不展示
          if (!data) return;

          if (data?.creative?.length) {
            swiperBannerList.value = data.creative.map((item) => {
              const {
                img: image,
                impId,
              } = item?.segment as Dictionary<string>;
              return {
                url: item.link as string,
                imageUrl: image,
                id: impId,
                name: impId,
              };
            });
            advData.value = data.creative;
            emit('init', getTrackingParams());
          }
        }).catch((error) => {
          ubtSendError({
            name: 'Failed to fetch adv data',
            message: error,
          });
          swiperBannerList.value = [];
        });
    };

    /**
     * 处理轮播图索引变化的回调函数
     *
     * @param index - 当前轮播图的索引值
     */
    const onBannerChange = (index: number) => {
      if (!swiperBannerList.value || index < 0 || index >= swiperBannerList.value?.length) {
        return;
      }

      // 更新当前轮播图的索引
      currentIndex.value = index;
      emit('indicator-change', getTrackingParams());
    };

    /**
     * 跳转到指定的轮播图链接
     *
     * @param url - 需要跳转的链接地址
     */
    const goBannerUrl = (url: string) => {
      emit('click-item', getTrackingParams());
      autoplay.value = 0;
      // 打开第三方页面
      openThirdPage(url);
    };

    /** 监听应用返回事件，并在返回时执行继续轮播 */
    listenAppBackToApp(() => {
      // 当应用返回时，将 autoplay 的值设置为 true
      autoplay.value = loopTime;
    });

    initData();

    return {
      swiperBannerList,
      autoplay,
      onBannerChange,
      goBannerUrl,
    };
  },
});
